import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, FolderTree, CheckSquare, Square, Trash } from 'lucide-react';
import { categoriasApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { Categoria } from '@/types';


export function Categorias() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ nome: '', slug: '', cor: '#FF6B35' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: categorias, isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: async () => {
      const response = await categoriasApi.listar();
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as Categoria[];
      }
      return (Array.isArray(data) ? data : []) as Categoria[];
    }
  });

  const criarMutation = useMutation({
    mutationFn: (data: any) => categoriasApi.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      setIsAdding(false);
      setFormData({ nome: '', slug: '', cor: '#FF6B35' });
    }
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => categoriasApi.atualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      setEditingId(null);
    }
  });

  const deletarMutation = useMutation({
    mutationFn: (id: string) => categoriasApi.deletar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Erro ao excluir categoria';
      alert(message);
    }
  });

  const bulkDeletarMutation = useMutation({
    mutationFn: (ids: string[]) => categoriasApi.bulkDeletar(ids),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['categorias'] });
      setSelectedIds([]);
      alert(response.data.message || 'Exclusão em massa concluída!');
    },
    onError: () => {
      alert('Erro ao realizar exclusão em massa');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      atualizarMutation.mutate({ id: editingId, data: formData });
    } else {
      criarMutation.mutate(formData);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === (categorias?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(categorias?.map(c => c.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir as ${selectedIds.length} categorias selecionadas?`)) {
      bulkDeletarMutation.mutate(selectedIds);
    }
  };

  if (isLoading) return <div className="p-8 text-center text-gray-500 text-lg font-medium animate-pulse">Carregando categorias...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categorias</h2>
          <p className="text-gray-500">Gerencie as categorias de produtos</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleBulkDelete}
              disabled={bulkDeletarMutation.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
          <Button onClick={() => setIsAdding(true)} disabled={isAdding}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Categoria
          </Button>
        </div>
      </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
              placeholder="Ex: Filamentos"
            />
            <Input
              label="Slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
              placeholder="ex-filamentos"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={formData.cor}
                  onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-200 p-1 bg-white cursor-pointer"
                />
                <span className="text-sm font-mono text-gray-500 uppercase">{formData.cor}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsAdding(false);
                setEditingId(null);
                setFormData({ nome: '', slug: '', cor: '#FF6B35' });
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={criarMutation.isPending || atualizarMutation.isPending}>
              {editingId ? 'Atualizar' : 'Salvar Categoria'}
            </Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden overflow-x-auto ring-1 ring-black ring-opacity-5">
        <table className="w-full text-left min-w-[600px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 w-10">
                <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                  {selectedIds.length === (categorias?.length || 0) && selectedIds.length > 0 ? (
                    <CheckSquare className="h-5 w-5 text-orange-600" />
                  ) : (
                    <Square className="h-5 w-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Categoria</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Slug</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {categorias?.map((cat) => (
              <tr key={cat.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(cat.id) ? 'bg-orange-50/50' : ''}`}>
                <td className="px-6 py-4">
                  <button onClick={() => toggleSelect(cat.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                    {selectedIds.includes(cat.id) ? (
                      <CheckSquare className="h-5 w-5 text-orange-600" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 shadow-sm"
                      style={{ backgroundColor: cat.cor || '#FF6B35' }}
                    >
                      <FolderTree className="h-5 w-5" />
                    </div>
                    <span className="font-semibold text-gray-900">{cat.nome}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-600">{cat.slug}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 hover:bg-orange-50 hover:text-orange-600 text-gray-500"
                      onClick={() => {
                        setEditingId(cat.id);
                        setFormData({ nome: cat.nome, slug: cat.slug, cor: cat.cor || '#FF6B35' });
                        setIsAdding(false);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 hover:bg-red-50 hover:text-red-600 text-gray-400"
                      onClick={() => {
                        if (window.confirm(`Deseja excluir a categoria "${cat.nome}"?`)) {
                          deletarMutation.mutate(cat.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!categorias || categorias.length === 0) && (
          <div className="p-12 text-center">
            <FolderTree className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Nenhuma categoria encontrada</p>
          </div>
        )}
      </div>
    </div>
  );
}
